// Unified account-owner chip (Customer 360 spec §7A). ONE visual for "who owns
// this" — crown avatar + primary name — rendered identically on deal cards,
// deal/lead table rows, the quote header and the 360 identity bar. Display-only;
// editing still lives in AccountTeamCard.
import { Crown } from "lucide-react";

// Deterministic avatar tint from a name, so the same person is the same colour
// everywhere in the app.
const TINTS = [
  "bg-teal-500/20 text-teal-200 border-teal-400/30",
  "bg-blue-500/20 text-blue-200 border-blue-400/30",
  "bg-amber-500/20 text-amber-200 border-amber-400/30",
  "bg-violet-500/20 text-violet-200 border-violet-400/30",
  "bg-rose-500/20 text-rose-200 border-rose-400/30",
  "bg-emerald-500/20 text-emerald-200 border-emerald-400/30",
];
function tintFor(name = "") {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return TINTS[h % TINTS.length];
}
function initials(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

// size: "xs" (rows/cards) | "sm" (headers/identity bar)
export function OwnerChip({ primary, size = "xs", onAssign, className = "" }) {
  const dims = size === "sm"
    ? { av: "w-6 h-6 text-[10px]", name: "text-[12px]", gap: "gap-1.5" }
    : { av: "w-5 h-5 text-[9px]", name: "text-[11px]", gap: "gap-1.5" };

  if (!primary) {
    const base = "inline-flex items-center gap-1 rounded-full border border-dashed border-amber-500/40 text-amber-300/80 px-2 py-0.5 text-[11px]";
    return onAssign ? (
      <button type="button" onClick={onAssign} className={`${base} hover:bg-amber-500/10 ${className}`} data-testid="owner-chip-assign">
        <Crown className="w-3 h-3" /> Assign Account Manager
      </button>
    ) : (
      <span className={`${base} ${className}`} data-testid="owner-chip-empty">
        <Crown className="w-3 h-3" /> Unassigned
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center ${dims.gap} min-w-0 ${className}`}
      title={primary}
      data-testid="owner-chip"
    >
      <span className={`relative grid place-items-center rounded-full border font-semibold shrink-0 ${dims.av} ${tintFor(primary)}`}>
        {initials(primary)}
        <Crown className="absolute -top-1.5 -right-1 w-2.5 h-2.5 text-amber-300" />
      </span>
      <span className={`text-slate-200 font-medium truncate ${dims.name}`}>{primary}</span>
    </span>
  );
}

export default OwnerChip;
